'use client';

import * as React from 'react';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cn } from './utils';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

type RadioGroupItemProps = {
  usePixelatedIcon?: boolean;
} & React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>;

const RadioGroupItem = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioGroupItemProps>(
  ({ className, value, onClick, usePixelatedIcon = false, ...props }, ref) => {
    return (
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(
          'aspect-square h-4 w-4 text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          !usePixelatedIcon && cn('rounded-full border border-primary'),
          className,
        )}
        value={value}
        onClick={onClick}
        {...props}
      >
        <div className="relative flex justify-center items-center">
          {usePixelatedIcon ? (
            <>
              {/* Unselected state - always visible */}
              <div className="h-2.5 w-2.5 rounded-sm border border-current" />
              {/* Selected state - only visible when item is checked */}
              <RadioGroupPrimitive.Indicator className="absolute flex items-center justify-center">
                <div className="h-2.5 w-2.5 rounded-sm bg-current" />
              </RadioGroupPrimitive.Indicator>
            </>
          ) : (
            <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
              <Circle className="h-2.5 w-2.5 fill-current text-current" />
            </RadioGroupPrimitive.Indicator>
          )}
        </div>
      </RadioGroupPrimitive.Item>
    );
  },
);
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
